import { Popup } from 'components/Popup'
import { FE_GATEWAY_API_URL } from 'config/app'
import { useFormikContext } from 'formik'
import API from 'lib/api'
import React, { useContext, useEffect, useState } from 'react'
import { Button, Text, View } from '../ui'
import { PreorderInvoice } from './PreorderInvoice'
import { SplitContext } from 'containers/SplitContext'
import SPLIT_FEATURES from 'containers/SplitContext/features'
import {
  errorToast,
  generateQRFromPLU,
  groupCart,
  htmlToPreorderPdf,
  refreshedOrderDetail,
  xUserHeaders,
} from '../helper'

const removeFocAndUnchecked = ({ serverCart, localCart }) =>
  serverCart.reduce((acc, item) => {
    if (!item.isFree && localCart[item.product.clientItemId]) {
      acc.push(item)
    }
    return acc
  }, [])

const removeUncheckedItems = ({ serverCart }) =>
  // server cart contains unchecked item and FOC (checked)
  serverCart.reduce((acc, item) => {
    if (item.isChecked === true) {
      acc.push(item)
    }
    return acc
  }, [])

// Accepts either shape the receipt can be built from. Cart items (`product`/`q`)
// keep their quantity verbatim, exactly as before; only order-service items
// (`item`/`orderedQuantity`) are coerced, because that endpoint returns
// "1.00000" where the PLU needs "1".
export const generateQR = (orderId, printItems, options) => {
  const allPLU = printItems.map((item) => {
    const itemDetail = item.product || item.item
    const quantity =
      item.q === undefined ? Number(item.orderedQuantity) : item.q
    return `${quantity}x${itemDetail.clientItemId}`
  })
  return generateQRFromPLU(orderId, allPLU, options)
}

// What the order actually holds once the server has applied stock limits and
// awarded gifts — the cart only knows what was asked for. Read back exactly the
// way ViewOrder does, so the first print and the re-print agree by construction.
const fetchPrintableOrder = async (orderId) => {
  const { deliveryOrder } = await refreshedOrderDetail(orderId)
  return {
    order: deliveryOrder,
    items: groupCart(
      deliveryOrder.items,
      'item.clientItemId',
      'orderedQuantity'
    ).concat(deliveryOrder.freeItems ?? []),
  }
}

export const ConfirmOrderModal = ({
  isOpen,
  toggleModal,
  onDone,
  storeName,
  storeAddress,
  storePhone,
  cart,
  setSelectedCampaign,
}) => {
  const { values } = useFormikContext()
  const { splits } = useContext(SplitContext)
  const [loading, setLoading] = useState(false)
  // The order, its rows and the QR encoded from those same rows, resolved once
  // and held together — the invoice can then never render a mix of them, nor
  // re-derive its rows from a `cart` prop that has moved on since the click.
  const [printJob, setPrintJob] = useState(null)
  const printFromOrderDetail =
    splits?.[SPLIT_FEATURES.PREORDER_PRINT_FROM_ORDER_DETAIL]?.treatment ===
    'on'
  const checkedItemsWithoutFoc = removeFocAndUnchecked({
    serverCart: cart,
    localCart: values.cart,
  })
  const checkedItemsWithFoc = removeUncheckedItems({
    serverCart: cart,
  })
  // With the flag off this component has to behave exactly as it did before the
  // read-back existed — rows re-derived from the cart as the invoice renders,
  // and nothing printed when there are no QR codes. The snapshot that closes the
  // gap between the click and the print is part of the new path, not a
  // correctness fix applied to both, so that off is a true rollback.
  const printRows = printFromOrderDetail ? printJob?.items : checkedItemsWithFoc
  const canPrint = printFromOrderDetail
    ? Boolean(printJob)
    : Boolean(printJob?.qrImages.length)

  const createOrder = () => {
    const createOrderPayload = {
      addressId: 0,
      campaignId: Number(values.selectedCampaign), // must be number, not string
      metaData: {
        staffId: values.staffId,
      },
      cart: checkedItemsWithoutFoc.map(({ id, q }) => ({ id, q })),
      customerId: values.userData.userId,
      isCheckerEnabled: true,
      isDirectFulfilmentEnabled: true,
      isOutOfStockEnabled: true,
      isSellerCheckerEnabled: true,
      isSellerTimeSlotEnabled: true,
      orderType: 'RB_PREORDER',
      paymentMethod: 'OFFLINE',
      sellerTimeSlots: [
        {
          preferredDate: values.pickupDate,
          preferredSlotId: values.slotId,
        },
      ],
      storeId: Number(values.storeId),
      type: 'RB_PREORDER',
    }
    setLoading(true)

    new API({ url: `${FE_GATEWAY_API_URL}/order` })
      .post(
        createOrderPayload,
        xUserHeaders({
          userId: values.userData.userId,
          userUid: values.userData.userUid,
        })
      )
      .then(async (res) => {
        const createdOrder = res.data?.order
        const orderId = createdOrder?.referenceNumber
        // Flag off keeps the cart as the source, exactly as before.
        let printable = { order: createdOrder, items: checkedItemsWithFoc }
        try {
          if (printFromOrderDetail) {
            printable = await fetchPrintableOrder(orderId)
          }
        } catch (e) {
          // The order exists whether or not it can be read back, so never block
          // the receipt on this — fall back to the cart the staff just
          // confirmed. Logged rather than swallowed, because that receipt is the
          // one that is not guaranteed to match what the order holds.
          // eslint-disable-next-line no-console
          console.error(
            `[PREORDER] could not read back order ${orderId}, printing from the cart instead: ${e.message}`
          )
        }
        const qrImages = await generateQR(orderId, printable.items, {
          pluPerQRFlag: splits?.[SPLIT_FEATURES.PREORDER_PLU_PER_QR],
        })
        setPrintJob({ ...printable, qrImages })
      })
      .catch((e) => {
        errorToast('CREATE_ORDER_FAILURE', e.message)
        toggleModal()
        setLoading(false)
      })
  }

  useEffect(() => {
    if (canPrint) {
      preparePrint(printJob)
    }
  }, [printJob])

  const preparePrint = ({ order }) => {
    const input = document.getElementById('preorderInvoice')

    htmlToPreorderPdf({
      input,
      orderId: order.referenceNumber,
    })
      .then(() => {
        setPrintJob(null)
        /**
         * Commented out this - if receipt printing succes, dont
         * close the modal unless we wanna show `SUCCESS` dialog or something.
         */
        // toggleModal()
        // onDone()
        /**
         * As per the latest fixed, this code wont clear cart, instead
         * will be calling onDone() to clear cart instead
         */
        setSelectedCampaign(null)
        /**
         * Immediately complete the flow and go back to
         * Home page + clear Cart. This will prevent user from getting stuck at the
         * confirm order or the confirm modal for too long.
         */
        onDone()
      })
      .catch((e) => {
        errorToast('PRINT_RECEIPT_FAILURE', e.message)
        setLoading(false)
      })
  }

  return (
    <>
      <Popup
        show={isOpen}
        close={() => !loading && toggleModal()}
        heading="Confirm order?"
        className="confirmPreorderPopup"
      >
        <View
          direction="column"
          alignItems="start"
          style={{ fontFamily: 'Roboto' }}
        >
          Review order details and select ‘Print QR code’ to confirm order.
          <Text fontWeight={700} margin="16px 0 0">
            Order summary:
          </Text>
          {Object.entries(values.products).reduce(
            (t, [sku, q]) => t + (values.cart[sku] ? q : 0),
            0
          )}{' '}
          items
          <Text fontWeight={700} margin="16px 0 0">
            Customer details:
          </Text>
          <Text>Name: {values.customerName}</Text>
          <Text>Mobile: {values.customerPhone}</Text>
          <Text fontWeight={700} margin="16px 0 0">
            Pick-up location:
          </Text>
          <View direction="column" alignItems="start">
            <Text>{storeName}</Text>
            <Text textAlign="left">{storeAddress}</Text>
          </View>
          <Text fontWeight={700} margin="16px 0 0">
            Pick-up time slot:
          </Text>
          <Text>
            {values.pickupDate}, {values.pickupTime}
          </Text>
          <View gap={8} margin="16px 0 0">
            <Button
              fontSize={12}
              variant="cancel"
              onClick={toggleModal}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button fontSize={12} onClick={createOrder} loading={loading}>
              Print QR code
            </Button>
          </View>
        </View>
      </Popup>
      {canPrint ? (
        <PreorderInvoice
          qrSources={printJob.qrImages}
          order={printJob.order}
          cartItems={printRows}
          otherCartDetails={{
            customerName: values.customerName,
            customerPhone: values.customerPhone,
            pickupDate: values.pickupDate,
            pickupTime: values.pickupTime,
            staffId: values.staffId,
            storePhone,
            storeName,
            storeAddress,
          }}
        />
      ) : null}
    </>
  )
}
